import { AfterViewInit, Component, ElementRef, Input, ViewChild } from '@angular/core';
import { NgxQrcodeElementTypes, NgxQrcodeErrorCorrectionLevels, QrcodeComponent } from '@techiediaries/ngx-qrcode';
import { HotToastService } from '@ngneat/hot-toast';

import { Channel } from '@portals/api';
import { environment } from '@portals/environments';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ChannelsState } from '@portals/store';
import { Notification } from '@portals/api';
import { BaseComponent } from '@portals/core';
import { tap } from 'rxjs';
import { ChannelFeedComponent } from '../channel-feed';
import * as SVG from 'svg.js';
import * as moment from 'moment';

@Component({
	selector: 'channel-preview',
	templateUrl: './channel-preview.component.html',
	styleUrls: ['./channel-preview.component.scss'],
})
export class ChannelPreviewComponent extends BaseComponent implements AfterViewInit {
	@ViewChild('label') label!: ElementRef<HTMLElement>;
	@ViewChild('feedIcon', { static: true }) feedIconRef!: ElementRef;
	@ViewChild('qrCodeContainer') qrCodeContainer!: ElementRef;
	@ViewChild('qrCode') qrCode!: QrcodeComponent;
	@Input() channel!: Channel;
	@Input() noBorder: boolean = false;
	@Input() drawer: boolean = false;

	private maxLines: number = 2;
	private numLines!: number;
	private fontSize!: number;

	qrcode!: any;
	app = environment.app;
	isAppJotto = environment.app == 'Jotto';
	elementType = NgxQrcodeElementTypes.IMG;
	correctionLevel = NgxQrcodeErrorCorrectionLevels.HIGH;
	headerTitle = !this.isAppJotto ? 'follow' : 'follow with';
	projectImage = '/assets/images/default.png';
	previewDialog!: MatDialogRef<any>;
	qrCodeColor = {
		dark: '#491176',
		light: '#fbd354',
	};
	notifs = false;
	notifications: Array<Notification> = [];

	constructor(private toast: HotToastService, private channelsState: ChannelsState, private dialog: MatDialog) {
		super();
	}

	ngAfterViewInit() {
		setTimeout(() => {
			this.fontSize = parseInt(window.getComputedStyle(this.label.nativeElement).fontSize);
			this.numLines = this.label.nativeElement.clientHeight / this.fontSize;

			if (this.numLines >= this.maxLines) {
				this.label.nativeElement.style.fontSize = `${this.fontSize - 10}px`;
			}

			this.qrcode = document.getElementsByClassName('qrcode')[0]?.children[0]?.getAttribute('src');
		}, 50);

		this.subscribe(
			this.channelsState.findOnebyUUID(<string>this.channel.uuid).pipe(
				tap(async (data: any) => {
					this.channel = data;
					this.notifications = [
						...data.notifications
							.filter((notification: Notification) => notification.type === 'channel')
							.sort((a: Notification, b: Notification) => {
								return b.orderIndex - a.orderIndex;
							}),
					];
					this.notifs = this.notifications?.length > 0;
				})
			)
		);
	}

	shareConfirm() {
		const toaster = this.toast.success('Channel share url copied to your clipboard!', {
			style: {
				border: `4px solid var(--portals-theme-popover)`,
				padding: '16px',
				margin: '20px 20px',
				color: 'var(--portals-theme-on-popover)',
				background: 'var(--portals-theme-popover)',
			},
			iconTheme: {
				primary: 'var(--portals-theme-primary)',
				secondary: 'var(--portals-theme-accent)',
			},
		});
		setTimeout(async () => {
			await toaster.close();
			this.toast.close();
		}, 1000);
	}

	print() {
		const printContent1 = document.getElementById('qr-code-print');
		const printContent2 = document.getElementById('share-url-print');
		const windowUrl = 'about:blank';
		const uniqueName = new Date().getTime().toString();
		const printWindow = window.open(windowUrl, uniqueName);
		if (printWindow && printContent1 && printContent2) {
			printWindow.document.write(
				`<html>
          <head>
          <style>
            body {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              height: 100vh;
            }

            #qr-code-print, #share-url-print {
              width: 50%;
              text-align: center;
            }
          </style>
          </head>
          <body>
            ${printContent1.outerHTML}


            ${printContent2.outerHTML}
          </body>
        </html>`
			);
			printWindow.document.close();
			printWindow.focus();
			printWindow.print();
			printWindow.close();
		}
	}

	openDialog() {
		if (this.notifications && this.notifications.length > 0) {
			this.previewDialog = this.dialog.open(ChannelFeedComponent, {
				data: {
					notifications: this.notifications,
					channel: this.channel,
					portalUser: true,
				},
				width: 'auto',
				maxWidth: '100%',
				height: '610px',
				panelClass: 'intake-notification-dialog',
				backdropClass: 'backdropBackground',
				closeOnNavigation: true,
				autoFocus: false,
				restoreFocus: false,
			});
		}
	}

	compositeAndDownload() {
		// Create an SVG container
		const draw = SVG(this.qrCodeContainer.nativeElement).size(300, 300);

		// Load the main SVG (rectangle) from the internet
		const rectangleUrl = this.qrCode.qrcElement.nativeElement.innerHTML; // Replace with the actual URL
		draw.svg(rectangleUrl);

		// Load the image from the internet as an SVG
		const imageUrl = '/assets/images/jotto-yellow.svg'; // Replace with the actual URL
		const imageSvgString = this.loadSvgString(imageUrl);
		draw.svg(imageSvgString);

		// Convert the composed SVG to a data URI
		const composedSvgDataUri = draw.svg();
		const composedSvgBlob = new Blob([composedSvgDataUri], {
			type: 'image/svg+xml',
		});
		const composedSvgUrl = URL.createObjectURL(composedSvgBlob);

		// Create a download link
		const downloadLink = document.createElement('a');
		downloadLink.href = composedSvgUrl;
		downloadLink.download = `${this.channel.name}.svg`;

		// Simulate a click to trigger the download
		downloadLink.click();

		// Clean up
		URL.revokeObjectURL(composedSvgUrl);
		draw.clear();
	}

	private loadSvgString(url: string): string {
		const xhr = new XMLHttpRequest();
		xhr.open('GET', url, false);
		xhr.send();
		return xhr.responseText;
	}
}
